import type { Locale } from "./i18n";

/**
 * The one list of file types the app's file inputs accept.
 *
 * Found by an independent review on 2026-08-27: `HomeNext.tsx` and
 * `CompareView.tsx` each spelled out this string by hand, and it had already
 * drifted once. Both inputs accept `.pst`/`.ost`/`.zip` alongside `.eml`,
 * `.mbox` and `.msg`, but Compare's own drop-zone COPY still named only the
 * first three, so a reader who read the hint before dropping a file was told
 * a narrower capability than the `accept` attribute two lines below it grants.
 *
 * One constant means the two can no longer say different things about the
 * same input element. `about.ts` and `faq.ts` describe capability in prose at
 * a different altitude (what the parser understands, not which file picker
 * dialog filters appear), so their sentences are still written by hand rather
 * than assembled from this string. What they may not do is name a DIFFERENT
 * set: both had drifted narrow (the FAQ answer to "What files can I open?"
 * named two of the six, About left out `.pst`/`.ost` while the app's own
 * roadmap called them shipped), so `content/format-claims.test.ts` now holds
 * the extensions they name to the list below in both directions.
 */
export const SUPPORTED_FILE_ACCEPT = ".eml,.mbox,.msg,.pst,.ost,.zip,.txt,message/rfc822";

/**
 * The container formats a human names, derived from the accept string so the
 * two cannot drift.
 *
 * `.txt` is deliberately absent. It sits in `SUPPORTED_FILE_ACCEPT` as a
 * permissive fallback for a raw source saved with the wrong extension, not as
 * a distinct container the way the other six are, and no copy surface has ever
 * named it. The MIME type is absent for the same reason: it is a picker filter,
 * not a file extension a reader recognises.
 */
export const SUPPORTED_CONTAINER_EXTENSIONS: readonly string[] =
  SUPPORTED_FILE_ACCEPT.split(",")
    .filter((token) => token.startsWith(".") && token !== ".txt")
    .map((token) => token.slice(1));

/**
 * The mailbox intake's `accept`, which is the same containers plus two media
 * types the other inputs do not need.
 *
 * The docblock above said "the app's two file inputs" and it was one short:
 * `CorpusView` has a third, and it had already drifted from the constant that
 * exists to stop exactly that. It really does need `application/mbox` and
 * `text/plain`, since an mbox export routinely arrives with no extension or a
 * `.txt` one and a picker that filters by extension alone would grey it out,
 * but the container half is not its business to restate, so it is derived.
 */
export const MAILBOX_FILE_ACCEPT = [
  ...SUPPORTED_CONTAINER_EXTENSIONS.map((ext) => `.${ext}`),
  "application/mbox",
  "text/plain",
].join(",");

/** The extensions in `SUPPORTED_FILE_ACCEPT` a human reads, in one order. */
export const SUPPORTED_FILE_LABEL: Record<Locale, string> = {
  en: ".eml, .mbox, .msg, .pst, .ost, or .zip",
  de: ".eml, .mbox, .msg, .pst, .ost oder .zip",
};
